/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect } from 'react';
import {
  VictoryArea,
  VictoryVoronoiContainer,
  VictoryChart,
  VictoryAxis,
  VictoryTooltip,
  VictoryTheme,
  VictoryLabel,
} from 'victory';
import { format } from 'date-fns';
// import { tw } from 'utils';
import LineChartTooltip from '../LineChart/LineChartTooltip';
// import { useBaseCurrency } from '@hooks/useBaseCurrency';
// import { Defs, Stop, LinearGradient } from 'react-native-svg';
import { minBy, maxBy } from 'lodash';
import { formatCurrency } from 'utils/format';
import { AreaChartTooltip } from './AreaChartTooltip';

export const AreaChart = ({
  data,
  color,
  fillColor,
  onChangeValue,
  onChangePercentage,
  onHasScrolling,
  initialValue,
  initialPercentage,
  tooltipWithValue,
  graphHeight,
  gradient,
  absolute,

  // animation defaults
  animateDuration,
  toolTipBoxBorderRadius,
  toolTipBoxHeight,
  toolTipTrackerLineYValue,
  toolTipTrackerLineHeight,
  toolTipTrackerLineWidth,
  toolTipTextYValue,
  toolTipBoxYValue,
  chartBottomPadding,
  chartTopPadding,
  chartRightPadding,
  chartLeftPadding,
}: {
  data: {
    x: number | string,
    y: number;
  }[];
  color: string;
  fillColor?: string;
  hasMinDomain?: boolean;
  hasMaxDomain?: boolean;
  onChangeValue?: Function;
  onChangePercentage?: Function;
  // Sets whether the parent view has scrolling enabled or not (set with bool)
  onHasScrolling?: Function;
  initialValue?: number;
  initialPercentage?: number;
  tooltipWithValue?: any;
  graphHeight: number;
  gradient?: boolean;
  absolute?: boolean;

  // animation defaults
  animateDuration: number;
  toolTipBoxBorderRadius: number;
  toolTipBoxHeight: number;
  toolTipTrackerLineYValue: number;
  toolTipTrackerLineHeight: number;
  toolTipTrackerLineWidth: number;
  toolTipTextYValue: number;
  toolTipBoxYValue: number;
  chartBottomPadding: number;
  chartTopPadding: number;
  chartRightPadding: number;
  chartLeftPadding: number;
}) => {
  // #region HOOKS
  const baseCurrency = { code: 'ZAR' } //useBaseCurrency();
  const values = data.map(p => p.y).sort();
  const dateValues = data.map(p => p.x).sort((a,b) => { return new Date(a).getTime() - new Date(b).getTime() });
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  // #endregion

  const offset = 8;
  // #region HELPERS
  // @ts-ignore
  const getMinDataPoint = () => minValue / 1.001 - 5;
  // @ts-ignore
  const getMaxDataPoint = () => maxValue / 0.999 + 5;
  // @ts-ignore
  const getMinDatePoint = () => dateValues[0];
  // @ts-ignore
  const getMaxDatePoint = () => dateValues[dateValues.length - 1];
  // @ts-ignore
  const getMidDatePoint = () => dateValues[Math.floor(dateValues.length / 2)];
  // #endregion

  // #region HANDLERS
  const onStartHandler = useCallback(() => {
    onHasScrolling?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEndHandler = useCallback(() => {
    onHasScrolling?.(true);
    onChangeValue?.(initialValue);
    onChangePercentage?.(initialPercentage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeHandler = useCallback((item: any) => {
    onChangeValue?.(item.Value);
    onChangePercentage?.(item.Change);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  // #region LIFECYCLE
  useEffect(() => {
    // RS: Commented out console log for prod use
    // console.log('AreaChart.data:', data)
  }, [data])
  // #endregion

  return (
    <div
      style={{
        // ...tw(`overflow-hidden ${absolute ? 'absolute z-10 bottom-12' : ''}`),
      }}>
      {data.length > 1 && (
        <VictoryChart
          // @ts-ignore
          minDomain={{ y: getMinDataPoint() !== 0 ? getMinDataPoint() : -1 }}
          // @ts-ignore
          maxDomain={{ y: getMaxDataPoint() }}
          height={graphHeight}
          padding={{
            top: chartTopPadding,
            bottom: chartBottomPadding,
            left: chartLeftPadding,
            right: chartRightPadding,
          }}
          containerComponent={
            <VictoryVoronoiContainer
              voronoiDimension="x"
              labels={({ datum }) => `${formatCurrency(datum.y, baseCurrency?.code)}\n${format(new Date(datum.x), 'dd MMM yyyy')}`}
              labelComponent={
                <AreaChartTooltip
                  isCustom={true}
                  formatDate={(x: any) => format(new Date(x), 'dd MMM yyyy')}
                  formatPrice={(y: number) => formatCurrency(y, baseCurrency?.code)}
                  // formatDate={(x: number) => x}
                  // formatPrice={(y: number) => y}
                />
              }
            />
          }
        >
          {!fillColor && (
            <defs>
              <linearGradient id={`gradient_${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop
                  offset="0%"
                  stopColor={color}
                  stopOpacity={gradient ? 0.2 : 0.1}
                />
                <stop
                  offset="100%"
                  stopColor={color}
                  stopOpacity={gradient ? 0 : 0.1}
                />
              </linearGradient>
            </defs>
          )}

          <VictoryArea
            data={data}
            animate={{ duration: animateDuration }}
            interpolation="natural"
            style={{
              data: {
                fill: fillColor || `url(#gradient_${color})`,
                stroke: color,
                strokeWidth: 2,
              },
            }}
          />

          {/* START X-AXIS DATE LINES*/}
          <VictoryAxis
             style={{
              axis: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
              ticks: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
              tickLabels: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
            }}
            theme={VictoryTheme.material}
            // offsetX={window.screen.width / 2}
            offsetY={10}
            axisLabelComponent={
              <VictoryLabel dy={-40} dx={-175} angle={0} textAnchor="middle" labelPlacement="parallel" 
                style={{
                  fontFamily: 'Bozon',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6E7D9F',
                  fill: '#6E7D9F',
                  width: 10,
                  display: 'flex',
                  flexWrap: 'wrap',
                  textAlign: 'center'
                }} 
              />
            }
            standalone={false}
            label={`${format(new Date(getMinDatePoint()), 'dd MMM')}\n${format(new Date(getMinDatePoint()), 'yyyy')}`}
          />

          {
          format(new Date(getMidDatePoint()), 'dd MMM yyyy') !== format(new Date(getMinDatePoint()), 'dd MMM yyyy') &&
          format(new Date(getMidDatePoint()), 'dd MMM yyyy') !== format(new Date(getMaxDatePoint()), 'dd MMM yyyy')
           && (
            <VictoryAxis           
              style={{
                axis: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
                ticks: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
                tickLabels: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
              }}
              theme={VictoryTheme.material}
              // offsetX={window.screen.width / 2}
              offsetY={10}
              axisLabelComponent={
                <VictoryLabel dy={-40} dx={10} angle={0} textAnchor="middle" labelPlacement="parallel" 
                  style={{
                    fontFamily: 'Bozon',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6E7D9F',
                    fill: '#6E7D9F',
                    width: 10,
                    display: 'flex',
                    flexWrap: 'wrap',
                    textAlign: 'center'
                  }} 
                />
              }
              standalone={false}
              label={`${format(new Date(getMidDatePoint()), 'dd MMM')}\n${format(new Date(getMidDatePoint()), 'yyyy')}`}
            />
          )}
          
          <VictoryAxis
            style={{
              axis: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
              ticks: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
              tickLabels: { stroke: 'transparent', fill: 'transparent', strokeWidth: 0, },
            }}
            theme={VictoryTheme.material}
            // offsetX={window.screen.width / 2}
            offsetY={10}
            axisLabelComponent={
              <VictoryLabel dy={-40} dx={190} angle={0} textAnchor="middle" labelPlacement="parallel" 
                style={{
                  fontFamily: 'Bozon',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6E7D9F',
                  fill: '#6E7D9F',
                  width: 10,
                  display: 'flex',
                  flexWrap: 'wrap',
                  textAlign: 'center'
                }} 
              />
            }
            standalone={false}
            label={`${format(new Date(getMaxDatePoint()), 'dd MMM')}\n${format(new Date(getMaxDatePoint()), 'yyyy')}`}
          />
          {/* END X-AXIS DATE LINES*/}
          
          {/* Y-AXIS VALUE LINES*/}
          <VictoryAxis
            dependentAxis
            tickValues={[getMinDataPoint(), ((getMinDataPoint() + getMaxDataPoint()) / 2), getMaxDataPoint()]}
            tickFormat={(t) => `${formatCurrency(t, baseCurrency?.code, 0, true)}`}
            maxDomain={{x: getMaxDataPoint()}}
            minDomain={{x: getMinDataPoint()}}
            orientation='right'
            offsetX={50}
            style={{
              axis: { stroke: 'transparent', strokeWidth: 0, },
              ticks: { stroke: 'transparent', strokeWidth: 0, },
              tickLabels: { 
                fontFamily: 'Bozon',
                fontSize: '12px',
                fontWeight: 600,
                color: '#6E7D9F',
                fill: '#6E7D9F' 
              },
            }}
          />

          {/* GREY X-AXIS LINES*/}
          <VictoryAxis
            dependentAxis
            tickValues={[getMinDataPoint()]}
            maxDomain={{x: getMaxDataPoint()}}
            minDomain={{x: getMinDataPoint()}}
            orientation='top'
            offsetY={graphHeight - 40}
            axisLabelComponent={
              <VictoryLabel dy={-25} dx={0} angle={0} textAnchor="end" labelPlacement="vertical" 
                style={{
                  fontFamily: 'Bozon',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6E7D9F',
                  stroke: '#6E7D9F',
                  zIndex: 0,
                }}  
              />
            }
            style={{
              axis: { stroke: '#6E7D9F', strokeWidth: 0.5, zIndex: 0 },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: 'transparent' },
            }}
          />

          <VictoryAxis
            dependentAxis
            tickValues={[getMaxDataPoint()]}
            maxDomain={{x: getMaxDataPoint()}}
            minDomain={{x: getMinDataPoint()}}
            orientation='top'
            offsetY={20}
            axisLabelComponent={
              <VictoryLabel dy={-25} dx={0} angle={0} textAnchor="start" labelPlacement="vertical" 
                style={{
                  fontFamily: 'Bozon',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6E7D9F',
                  stroke: '#6E7D9F',
                  zIndex: 0,
                }}  
              />
            }
            style={{
              axis: { stroke: '#6E7D9F', strokeWidth: 0.5, zIndex: 0 },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: 'transparent' },
            }}
          />

          <VictoryAxis
            dependentAxis
            tickValues={[getMaxDataPoint()]}
            maxDomain={{x: getMaxDataPoint()}}
            minDomain={{x: getMinDataPoint()}}
            orientation='top'
            offsetY={graphHeight / 2 - 10}
            axisLabelComponent={
              <VictoryLabel dy={-25} dx={0} angle={0} textAnchor="end" labelPlacement="vertical" 
                style={{
                  fontFamily: 'Bozon',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6E7D9F',
                  stroke: '#6E7D9F',
                  zIndex: 0,
                }}  
              />
            }
            style={{
              axis: { stroke: '#6E7D9F', strokeWidth: 0.5, zIndex: 0 },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: 'transparent' },
            }}
          />
        </VictoryChart>
      )}
    </div>
  );
};

AreaChart.defaultProps = {
  animateDuration: 400,
  chartBottomPadding: 40,
  chartTopPadding: 20,
  chartLeftPadding: 0,
  chartRightPadding: 50,
  graphHeight: 196,
  toolTipTrackerLineYValue: 36,
  toolTipTrackerLineHeight: 170,
  toolTipTrackerLineWidth: 0.5,
  toolTipBoxBorderRadius: 16,
  toolTipBoxHeight: 32,
  toolTipBoxYValue: 0,
  toolTipTextYValue: 8,
};

export default AreaChart;

